<template>
  <div
    :key="mediaItem.mobile"
    ref="item"
    :class="[
      [$style['media-grid-item']],
      {[$style['media-grid-item--video']]: mediaItem.type === 'video'}
    ]"
    :style="{'background-image': mediaItem.type === 'video' ? `url(${config.public.cdnURL}pictures/video_thumbs/${mediaItem.id}.webp)` : null}">
    <LazyVideoPlayer v-if="mediaItem.type === 'video'" :src="`${config.public.bunnyVideoURL}${mediaItem.id}/playlist.m3u8`" />
    <SmartImage
      v-else
      decoding="async"
      :loading="loadingMode(index)"
      :alt="alt"
      sizes="100vw"
      width="1200"
      height="1920"
      :srcset="mediaItem.full + ' 1920w, ' + mediaItem.mobile + ' 600w'"
      :src="mediaItem.mobile"
      @load="loaded = true"
      @touchstart="onTouchStart"
      @touchmove="onTouchMove"
      @touchend="onTouchEnd" />
    <div
      v-if="mediaItem.meta"
      :class="$style['media-grid-item__meta-button']"
      @click="showMeta = !showMeta">
      <svg-icon name="System/ruler-line" />
    </div>
    <div
      v-if="showMeta"
      :class="$style['media-grid-item__meta-info']">
      <p class="test">
        {{ $t('product/model-height') }} <b>{{ modelInfo.height }} cm</b>
      </p>
      <p class="test">
        {{ $t('product/model-size') }} <b>{{ modelInfo.size }}</b>
      </p>
    </div>
  </div>
</template>
<script setup>
const config = useRuntimeConfig()

await useI18n(['product'])
const props = defineProps({
  mediaItem: {
    type: Object,
    default: null
  },
  alt: {
    type: String,
    required: false,
    default: null
  },
  index: {
    type: Number,
    default: null
  }
})

const loaded = ref(false)
const modelInfo = props.mediaItem?.meta?.boundingBoxes[0]?.model

const showMeta = ref(false)
let start = { x: 0, y: 0, distance: 0 } /* eslint-disable-line */
let imageElementScale = 1

const distance = event => {
  return Math.hypot(event.touches[0].pageX - event.touches[1].pageX, event.touches[0].pageY - event.touches[1].pageY)
}

const onTouchStart = event => {
  if (event.touches.length === 2) {
    event.preventDefault() // Prevent page scroll

    // Calculate where the fingers have started on the X and Y axis
    start.x = (event.touches[0].pageX + event.touches[1].pageX) / 2
    start.y = (event.touches[0].pageY + event.touches[1].pageY) / 2
    start.distance = distance(event)
  }
}

const onTouchMove = event => {
  const imageElement = event.target

  if (event.touches.length === 2) {
    event.preventDefault() // Prevent page scroll

    // Safari provides event.scale as two fingers move on the screen
    // For other browsers just calculate the scale manually
    let scale
    if (event.scale) {
      scale = event.scale
    } else {
      const deltaDistance = distance(event)
      scale = deltaDistance / start.distance
    }
    imageElementScale = Math.min(Math.max(1, scale), 4)

    // Calculate how much the fingers have moved on the X and Y axis
    const deltaX = (((event.touches[0].pageX + event.touches[1].pageX) / 2) - start.x) * 2 // x2 for accelarated movement
    const deltaY = (((event.touches[0].pageY + event.touches[1].pageY) / 2) - start.y) * 2 // x2 for accelarated movement

    // Transform the image to make it grow and move with fingers
    const transform = `translate3d(${deltaX}px, ${deltaY}px, 0) scale(${imageElementScale})`
    imageElement.style.transform = transform
    imageElement.style.WebkitTransform = transform
    imageElement.style.zIndex = '9999'
  }
}

const onTouchEnd = event => {
  const imageElement = event.target
  imageElement.style.transform = ''
  imageElement.style.WebkitTransform = ''
  imageElement.style.zIndex = ''
}

const loadingMode = index => {
  return index < 1 ? 'auto' : 'lazy'
}
</script>
<style lang="scss" module>
.media-grid-item {
  @include backgroundPulse;

  /* stylelint-disable no-duplicate-selectors */
  & {
    aspect-ratio: 5/8;
    position: relative;
    background-size: cover;
    background-repeat: no-repeat;
    overflow: hidden;
    width: 100%;
  }

  &--video {
    animation: unset;
    transform: translateZ(1);
  }

  @media (min-width: #{$breakpoint-portrait-tablet}) {
    border-radius: var(--border-radius);
  }

  img {
    background-color: var(--grey-lighter);
    color: var(--grey-lighter); // Hides possible alt text
    border: 0;
    opacity: 1;
    vertical-align: middle;
    height: 100%;
    width: 100%;

    &.hidden {
    opacity: 0;
    }
  }

  &__spinner {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    left: 0;
    top: 0;
    z-index: 1;
    width: 100%;
    height: 100%;
  }

  &__meta-button {
    position: absolute;
    z-index: 500;
    bottom: 3rem;
    right: 1.5rem;
    width: 2.6rem;
    height: 2.6rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--spacing);
    color: var(--white);
    background-color: var(--black-20-opacity);
    border: 2px solid var(--grey-opacity);
    cursor: pointer;

    svg {
      width: 1.7rem;
      height: 1.7rem;
    }
  }

  &__face-box {
    position: absolute;
  }

  &__meta-info-container {
    position: relative;
    width: 100%;
    height: 100%;
  }

  &__meta-info {
    position: absolute;
    background: var(--white);
    white-space: nowrap;
    color: var(--black);
    padding: calc(var(--spacing) * 3) calc(var(--spacing) * 3.25);
    right: calc(var(--spacing) * 12);
    bottom: calc(var(--spacing ) * 4);
    border-radius: var(--spacing);
    border: 1px solid var(--grey-lighter);
    z-index: 1;

    & p {
      margin-bottom: var(--spacing);

      &:last-child {
        margin: 0;
      }
    }

    &::before {
      content: '';
      width: calc(var(--spacing) * 3);
      height: calc(var(--spacing) * 3);
      background-color: var(--white);
      border-radius: 4px;
      transform: rotate(45deg);
      position: absolute;
      right: calc(var(--spacing) * -1.25);
      bottom: calc(var(--spacing ) * 3);
      z-index: 0;
    }
  }
}
</style>
